/* eslint-disable no-nested-ternary */
import type { IGenericTreeNode } from './ix-generic-tree-types.js';

export const IconTypeMap = {
  Global: 'globe',
  Continent: 'map',
  Country: 'flag',
  City: 'domain',
  Campus: 'device_hub',
  Site: 'location_on',
  Floor: 'view_stream',
  Room: 'meeting_room',
  Cage: 'view_column',
  Row: 'view_headline',
  Cabinet: 'dock',
  Panel: 'calendar_view_day',
  Port: '',
  Fallback: 'chevron_right',
};

export function findChildNodeById(
  node: IGenericTreeNode,
  id: string,
): IGenericTreeNode | null {
  if (node.id === id) {
    return node;
  }
  if (!node.children) {
    return null;
  }

  for (const childNode of node.children) {
    const foundNode = findChildNodeById(childNode, id);

    if (foundNode) {
      return foundNode;
    }
  }

  return null;
}

export function updateNodeCheckedStatus(
  node: IGenericTreeNode,
  rootNode: IGenericTreeNode,
): void {
  /* eslint-disable no-param-reassign */
  function updateChildren(n: IGenericTreeNode, checked = false): void {
    n.indeterminate = false;
    if (n.children) {
      n.children.forEach(child => {
        child.inherited = checked;
        child.checked = false;
        updateChildren(child, checked);
      });
    }
  }

  function updateParents(n: IGenericTreeNode): void {
    if (!n.parentId) return;

    const currentParent = findChildNodeById(rootNode, n.parentId);
    if (!currentParent || !currentParent.children) return;

    const someChildrenChecked = currentParent.children.some(
      child => child.checked || child.indeterminate,
    );

    currentParent.indeterminate = someChildrenChecked;

    updateParents(currentParent);
  }

  // update all children to match current node checked status
  updateChildren(node, node.checked);

  // update all parents checked and indeterminate status
  updateParents(node);
}

export function getFlatArrayOfCheckedNodes(
  node: IGenericTreeNode,
): IGenericTreeNode[] {
  const checkedNodes: IGenericTreeNode[] = [];

  function getCheckedNodes(n: IGenericTreeNode): void {
    if (n.checked) {
      checkedNodes.push(n);
    }
    if (n.children) {
      n.children.forEach(child => {
        getCheckedNodes(child);
      });
    }
  }

  getCheckedNodes(node);
  return checkedNodes;
}

export function flattenTree(rootNode: IGenericTreeNode): IGenericTreeNode[] {
  const flatArray: IGenericTreeNode[] = [];

  function flatten(n: IGenericTreeNode): void {
    flatArray.push(n);
    if (n.children) {
      n.children.forEach(child => {
        flatten(child);
      });
    }
  }

  flatten(rootNode);
  return flatArray;
}

export function buildTreeFromFlatArray(
  flatArray: IGenericTreeNode[],
): IGenericTreeNode {
  const rootNode: IGenericTreeNode = {
    id: 'root',
    label: '',
    children: [],
  };

  rootNode.children = flatArray.filter(child => !child.parentId);

  function buildTree(n: IGenericTreeNode): void {
    const children = flatArray.filter(child => child.parentId === n.id);
    n.children = children;
    n.children.forEach(child => {
      buildTree(child);
    });
  }

  rootNode.children.forEach(child => {
    buildTree(child);
  });
  return rootNode;
}

export function isAnyNodeChecked(node: IGenericTreeNode): boolean {
  if (node.checked || node.indeterminate) {
    return true;
  }
  if (!node.children) {
    return false;
  }

  for (const childNode of node.children) {
    const checked = isAnyNodeChecked(childNode);

    if (checked) {
      return checked;
    }
  }

  return false;
}

export function resetCheckedAndIndeterminateStatus(
  node: IGenericTreeNode,
): void {
  node.checked = false;
  node.indeterminate = false;

  if (node.children) {
    node.children.forEach(child => {
      resetCheckedAndIndeterminateStatus(child);
    });
  }
}

export function getCheckedAndIndeterminateNodesOnly(
  node: IGenericTreeNode,
): IGenericTreeNode[] {
  const checkedNodes: IGenericTreeNode[] = [];

  function getCheckedNodes(n: IGenericTreeNode): void {
    if (n.checked || n.indeterminate || n.inherited) {
      checkedNodes.push(n);
    }
    if (n.children) {
      n.children.forEach(child => {
        getCheckedNodes(child);
      });
    }
  }

  getCheckedNodes(node);
  return checkedNodes;
}

export function findDeepestNode(
  root: IGenericTreeNode,
): IGenericTreeNode | null {
  let maxDepth = -1;
  let deepestNode = null;
  function dfs(node: IGenericTreeNode, depth: number) {
    if (!node) return;
    if (depth > maxDepth) {
      maxDepth = depth;
      deepestNode = node;
    }
    if (!node.children) return;
    for (const child of node.children) {
      dfs(child, depth + 1);
    }
  }
  dfs(root, 0);
  return deepestNode;
}

export function updateChildrenCount(rootNode: IGenericTreeNode): void {
  const deepestNode = findDeepestNode(rootNode);
  if (!deepestNode) return;

  function updateParents(n: IGenericTreeNode): void {
    if (!n.parentId) return;

    const currentParent = findChildNodeById(rootNode, n.parentId);
    if (!currentParent) return;
    currentParent.childrenCount = 0;
    if (currentParent.children) {
      currentParent.childrenCount += currentParent.children.reduce(
        (prev, curr) =>
          prev + (curr.childrenCount === 0 ? 1 : (curr.childrenCount ?? 0)),
        0,
      );
    }
    updateParents(currentParent);
  }

  updateParents(deepestNode);
}

export function getCheckedNodesCount(node: IGenericTreeNode): number {
  let count = 0;
  function getCheckedNodes(n: IGenericTreeNode): void {
    if (n.checked || n.inherited) {
      count += 1;
    }
    if (n.children) {
      n.children.forEach(child => {
        getCheckedNodes(child);
      });
    }
  }
  getCheckedNodes(node);
  return count;
}

export function collapseAllNodes(node: IGenericTreeNode): void {
  node.expanded = false;
  if (node.children) {
    node.children.forEach(child => {
      collapseAllNodes(child);
    });
  }
}

export function calculateCheckedEndNodesCount(node: IGenericTreeNode): number {
  let count = 0;
  function getCheckedEndNodes(n: IGenericTreeNode): void {
    if ((n.checked || n.inherited) && n.childrenCount === 0) {
      count += 1;
    }
    if (n.children) {
      n.children.forEach(child => {
        getCheckedEndNodes(child);
      });
    }
    if (!n.children?.length && n.childrenCount !== 0) {
      if (n.checked) count += n.childrenCount ?? 0;
      if (n.indeterminate) count += n.checkedChildrenCount ?? 0;
    }
  }
  getCheckedEndNodes(node);
  return count;
}
